import { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Activity, MapPin, Users, Clock, DollarSign, AlertTriangle,
  TrendingUp, TrendingDown, ChevronRight, RefreshCw, Search,
  Shield, Zap, Eye, Calendar, MessageSquare, Package, Star,
  ArrowUpRight, ArrowDownRight, X, Filter, Sparkles, Building2,
  UserCheck, ShoppingCart, Bell, ChevronDown, Lightbulb, Radio,
  Pause, Play, Headphones, Wrench, GraduationCap
} from "lucide-react";

const STATUS_ICON = { Strong: Shield, Stable: Activity, "Busy but Healthy": Zap, "Under Pressure": AlertTriangle, "At Risk": AlertTriangle, "Needs Attention": Bell };
const SORT_OPTIONS = [
  { value: "health", label: "Health Status" },
  { value: "revenue", label: "Revenue" },
  { value: "utilization", label: "Utilization" },
  { value: "alerts", label: "Most Alerts" },
  { value: "cancellations", label: "Cancellations" },
];

function PulseHealthBadge({ health }) {
  const Icon = STATUS_ICON[health.status] || Activity;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ backgroundColor: health.bg, color: health.color }}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2} />
      {health.status}
    </div>
  );
}

function MetricPill({ icon: Icon, label, value, color = "text-slate-700", iconBg = "bg-slate-100", iconColor = "text-slate-500" }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${iconColor}`} strokeWidth={1.5} />
      </div>
      <div>
        <p className={`text-sm font-semibold ${color} font-['Manrope']`}>{value}</p>
        <p className="text-[10px] text-slate-400 font-['Manrope'] uppercase tracking-wider">{label}</p>
      </div>
    </div>
  );
}

function AlertBadge({ alert }) {
  const colors = {
    danger: "bg-rose-50 text-rose-600 border-rose-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-blue-50 text-blue-600 border-blue-200",
    opportunity: "bg-violet-50 text-violet-600 border-violet-200",
    positive: "bg-emerald-50 text-emerald-600 border-emerald-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${colors[alert.type] || colors.info}`}>
      {alert.msg}
    </span>
  );
}

function ProgressBar({ value, max = 100, color = "#F43F5E" }) {
  const pct = Math.min(Math.round((value / max) * 100), 100);
  return (
    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: pct > 80 ? color : pct > 50 ? "#F59E0B" : "#94A3B8" }} />
    </div>
  );
}

function LocationCard({ loc, onClick }) {
  const PaceIcon = loc.revenue_pace_vs_last_week > 0 ? ArrowUpRight : ArrowDownRight;
  const paceColor = loc.revenue_pace_vs_last_week > 0 ? "text-emerald-500" : "text-rose-500";

  return (
    <div
      data-testid={`pulse-card-${loc.location_id}`}
      onClick={() => onClick(loc)}
      className="bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] cursor-pointer transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl gradient-primary flex items-center justify-center shadow-sm">
            <MapPin className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 tracking-tight">{loc.name}</h3>
            <p className="text-xs text-slate-400 font-['Manrope']">{loc.manager_on_duty ? `Mgr: ${loc.manager_on_duty}` : "No manager on duty"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PulseHealthBadge health={loc.health} />
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-rose-400 transition-colors" />
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-4 gap-4 mb-5">
        <MetricPill icon={Calendar} label="Booked" value={loc.booked_today} iconBg="bg-violet-50" iconColor="text-violet-500" />
        <MetricPill icon={Clock} label="Completed" value={loc.completed_today} iconBg="bg-emerald-50" iconColor="text-emerald-500" />
        <MetricPill icon={X} label="Cancelled" value={loc.cancel_count} iconBg="bg-rose-50" iconColor="text-rose-500" color={loc.cancel_count > 2 ? "text-rose-600" : "text-slate-700"} />
        <MetricPill icon={Eye} label="No-Shows" value={loc.noshow_count} iconBg="bg-amber-50" iconColor="text-amber-500" color={loc.noshow_count > 1 ? "text-amber-600" : "text-slate-700"} />
      </div>

      {/* Utilization & Revenue */}
      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className="bg-slate-50/60 rounded-xl p-3.5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope']">Utilization</p>
            <span className="text-sm font-semibold text-slate-800 font-['Manrope']">{loc.utilization}%</span>
          </div>
          <ProgressBar value={loc.utilization} />
          <p className="text-[10px] text-slate-400 mt-1.5 font-['Manrope']">{loc.open_gaps} open slots</p>
        </div>
        <div className="bg-slate-50/60 rounded-xl p-3.5">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope']">Revenue</p>
            {loc.revenue_pace_vs_last_week !== null && (
              <span className={`flex items-center gap-0.5 text-[10px] font-semibold ${paceColor}`}>
                <PaceIcon className="w-3 h-3" />
                {Math.abs(loc.revenue_pace_vs_last_week)}% vs LW
              </span>
            )}
          </div>
          <p className="text-lg font-semibold text-slate-900 font-['Outfit']">${loc.total_revenue.toLocaleString()}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <ProgressBar value={loc.target_progress} color="#8B5CF6" />
            <span className="text-[10px] text-slate-400 ml-1 font-['Manrope'] whitespace-nowrap">{loc.target_progress}% of target</span>
          </div>
        </div>
      </div>

      {/* Staffing */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 font-['Manrope']">
          <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
          {loc.staff_available}/{loc.staff_scheduled} staff
        </div>
        {loc.staff_callouts > 0 && (
          <span className="text-xs font-semibold text-rose-500 bg-rose-50 px-2 py-0.5 rounded-md">{loc.staff_callouts} call-out{loc.staff_callouts > 1 ? "s" : ""}</span>
        )}
        {loc.staff_late > 0 && (
          <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">{loc.staff_late} late</span>
        )}
        {loc.vip_count > 0 && (
          <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-md">{loc.vip_count} VIP</span>
        )}
      </div>

      {/* Escalation Issues */}
      {loc.escalation_breakdown?.total > 0 && (
        <div className="mb-4" data-testid={`esc-breakdown-${loc.location_id}`}>
          <div className="bg-gradient-to-r from-rose-50 to-violet-50 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-500" strokeWidth={2} />
                <span className="text-[10px] uppercase tracking-wider text-rose-600 font-bold font-['Manrope']">
                  {loc.escalation_breakdown.total} Open Issue{loc.escalation_breakdown.total > 1 ? "s" : ""}
                </span>
              </div>
              {loc.escalation_breakdown.urgent_count > 0 && (
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-rose-500 text-white">{loc.escalation_breakdown.urgent_count} URGENT</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(loc.escalation_breakdown.by_category || {}).map(([cat, count]) => {
                const labels = { client_complaint: "Complaints", inventory_shortage: "Inventory", equipment_issue: "Equipment", payroll_question: "Payroll", vip_concern: "VIP", redo_risk: "Redo Risk", shift_coverage: "Coverage", refund_request: "Refunds", manager_needed: "Manager", safety_incident: "Safety", front_desk_help: "Front Desk", general: "General" };
                return (
                  <span key={cat} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-white/80 text-slate-600 border border-slate-200/50">
                    {labels[cat] || cat}: {count}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {loc.alerts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {loc.alerts.slice(0, 3).map((a, i) => <AlertBadge key={i} alert={a} />)}
          {loc.alerts.length > 3 && <span className="text-xs text-slate-400 self-center font-['Manrope']">+{loc.alerts.length - 3} more</span>}
        </div>
      )}
    </div>
  );
}

function DetailDrawer({ loc, open, onClose }) {
  if (!loc) return null;
  const sections = [
    { title: "Schedule Health", icon: Calendar, iconBg: "bg-violet-50", iconColor: "text-violet-500", items: [
      { label: "Booked Today", value: loc.booked_today },
      { label: "Completed", value: loc.completed_today },
      { label: "Cancelled", value: loc.cancel_count },
      { label: "No-Shows", value: loc.noshow_count },
      { label: "Open Gaps", value: loc.open_gaps },
      { label: "Utilization", value: `${loc.utilization}%` },
      { label: "Avg Delay", value: `${loc.avg_delay} min` },
    ]},
    { title: "Staffing Health", icon: Users, iconBg: "bg-teal-50", iconColor: "text-teal-500", items: [
      { label: "Scheduled", value: loc.staff_scheduled },
      { label: "Available", value: loc.staff_available },
      { label: "Call-Outs", value: loc.staff_callouts, warn: loc.staff_callouts > 0 },
      { label: "Late Arrivals", value: loc.staff_late, warn: loc.staff_late > 0 },
      { label: "Manager on Duty", value: loc.manager_on_duty || "None" },
      { label: "VIPs In-House", value: loc.vip_count },
    ]},
    { title: "Revenue Health", icon: DollarSign, iconBg: "bg-rose-50", iconColor: "text-rose-500", items: [
      { label: "Service Revenue", value: `$${loc.service_revenue.toLocaleString()}` },
      { label: "Retail Revenue", value: `$${loc.retail_revenue.toLocaleString()}` },
      { label: "Total Revenue", value: `$${loc.total_revenue.toLocaleString()}` },
      { label: "Avg Ticket", value: `$${loc.avg_ticket}` },
      { label: "Daily Target", value: `$${loc.daily_target.toLocaleString()}` },
      { label: "Target Progress", value: `${loc.target_progress}%` },
      { label: "vs Last Week", value: loc.revenue_pace_vs_last_week !== null ? `${loc.revenue_pace_vs_last_week > 0 ? '+' : ''}${loc.revenue_pace_vs_last_week}%` : "N/A" },
    ]},
    { title: "Operations Health", icon: Package, iconBg: "bg-amber-50", iconColor: "text-amber-500", items: [
      { label: "Inventory Issues", value: loc.inventory_issues, warn: loc.inventory_issues > 0 },
      { label: "Equipment Issues", value: loc.equipment_issues, warn: loc.equipment_issues > 0 },
      { label: "Client Escalations", value: loc.client_escalations, warn: loc.client_escalations > 0 },
      { label: "Unresolved Tasks", value: loc.unresolved_tasks, warn: loc.unresolved_tasks > 0 },
    ]},
  ];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-white border-l border-slate-100 p-0 overflow-y-auto" data-testid="pulse-detail-drawer">
        <div className="p-8 space-y-6">
          <SheetHeader className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center shadow-sm">
                <MapPin className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <SheetTitle className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">{loc.name}</SheetTitle>
                <SheetDescription className="text-sm text-slate-400 font-['Manrope']">{loc.address}</SheetDescription>
              </div>
            </div>
            <PulseHealthBadge health={loc.health} />
          </SheetHeader>

          {/* Alerts */}
          {loc.alerts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-['Manrope']">Active Alerts</h4>
              <div className="flex flex-wrap gap-1.5">
                {loc.alerts.map((a, i) => <AlertBadge key={i} alert={a} />)}
              </div>
            </div>
          )}

          {/* Escalation Issues Breakdown */}
          {loc.escalation_breakdown?.total > 0 && (
            <div className="space-y-3" data-testid="drawer-escalations">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-500" strokeWidth={1.5} />
                </div>
                <h4 className="text-sm font-semibold text-slate-800 font-['Outfit']">
                  Escalated Issues ({loc.escalation_breakdown.total})
                </h4>
                {loc.escalation_breakdown.urgent_count > 0 && (
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-rose-500 text-white ml-auto">{loc.escalation_breakdown.urgent_count} URGENT</span>
                )}
              </div>
              {/* Category breakdown */}
              <div className="bg-rose-50/40 rounded-xl p-4 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(loc.escalation_breakdown.by_severity || {}).map(([sev, count]) => {
                    const sevColors = { critical: "text-rose-700 bg-rose-100", urgent: "text-red-600 bg-red-50", high: "text-orange-600 bg-orange-50", medium: "text-amber-600 bg-amber-50", low: "text-slate-500 bg-slate-100" };
                    return (
                      <div key={sev} className="flex items-center justify-between">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${sevColors[sev] || "bg-slate-100 text-slate-500"}`}>{sev}</span>
                        <span className="text-xs font-semibold text-slate-800">{count}</span>
                      </div>
                    );
                  })}
                </div>
                {/* Top issues list */}
                <div className="border-t border-rose-200/30 pt-3 space-y-2">
                  {(loc.escalation_breakdown.top_issues || []).map((issue, i) => {
                    const sevColor = { critical: "#BE123C", urgent: "#DC2626", high: "#EA580C", medium: "#D97706", low: "#64748B" };
                    const catIcons = { client_complaint: Headphones, inventory_shortage: Package, equipment_issue: Wrench, vip_concern: Star, redo_risk: AlertTriangle, payroll_question: DollarSign };
                    const IssueIcon = catIcons[issue.category?.replace(/ /g,"_")] || AlertTriangle;
                    return (
                      <div key={i} className="flex items-start gap-2.5 bg-white rounded-lg p-2.5 border border-slate-100">
                        <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${sevColor[issue.severity] || "#94A3B8"}15` }}>
                          <IssueIcon className="w-3 h-3" style={{ color: sevColor[issue.severity] || "#94A3B8" }} strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800 truncate font-['Manrope']">{issue.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[9px] font-bold uppercase" style={{ color: sevColor[issue.severity] }}>{issue.severity}</span>
                            <span className="text-[9px] text-slate-400">{issue.category}</span>
                            <span className="text-[9px] text-slate-400">· {issue.reporter}</span>
                          </div>
                        </div>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 font-medium capitalize">{issue.status?.replace("_"," ")}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <a href="/team-hub" className="flex items-center gap-1.5 text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors">
                <MessageSquare className="w-3.5 h-3.5" /> View all in Team Hub →
              </a>
            </div>
          )}

          {/* Detail Sections */}
          {sections.map(sec => (
            <div key={sec.title} className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-lg ${sec.iconBg} flex items-center justify-center`}>
                  <sec.icon className={`w-3.5 h-3.5 ${sec.iconColor}`} strokeWidth={1.5} />
                </div>
                <h4 className="text-sm font-semibold text-slate-800 font-['Outfit']">{sec.title}</h4>
              </div>
              <div className="bg-slate-50/60 rounded-xl p-4 grid grid-cols-2 gap-3">
                {sec.items.map(it => (
                  <div key={it.label} className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 font-['Manrope']">{it.label}</span>
                    <span className={`text-xs font-semibold font-['Manrope'] ${it.warn ? 'text-rose-600' : 'text-slate-800'}`}>{it.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Quick Actions */}
          <div className="space-y-3 pt-2 border-t border-slate-100">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-['Manrope']">Quick Actions</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: Calendar, label: "View Schedule", path: "/calendar" },
                { icon: Users, label: "View Staffing", path: "/staff" },
                { icon: MessageSquare, label: "Team Hub", path: "/team-hub" },
                { icon: Package, label: "Inventory", path: "/inventory" },
                { icon: DollarSign, label: "Revenue Report", path: "/reports" },
                { icon: Star, label: "Client Issues", path: "/score-alerts" },
              ].map(action => (
                <a key={action.label} href={action.path}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 font-medium font-['Manrope'] hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-all">
                  <action.icon className="w-4 h-4" strokeWidth={1.5} />
                  {action.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function LocationPulseBoardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("health");
  const [search, setSearch] = useState("");
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const prevStatusRef = useRef({});
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const fetchPulse = useCallback(async (isInitial = false) => {
    if (isInitial) setLoading(true);
    try {
      if (isInitial) { try { await api.post("/location-pulse/seed-ops"); } catch {} }
      const res = await api.get("/location-pulse");
      const newData = res.data;

      // Check for status drops → push notification toasts
      if (!isInitial && newData.locations) {
        for (const loc of newData.locations) {
          const prev = prevStatusRef.current[loc.location_id];
          const cur = loc.health.status;
          if (prev && prev !== cur) {
            const critical = ["At Risk", "Needs Attention"];
            const worsened = critical.includes(cur) && !critical.includes(prev);
            if (worsened) {
              toast.error(`${loc.name} status dropped to "${cur}"`, {
                description: loc.alerts[0]?.msg || "Immediate attention may be needed",
                duration: 8000,
              });
            } else if (cur === "Under Pressure" && prev !== "Under Pressure" && !critical.includes(prev)) {
              toast.warning(`${loc.name} is now "Under Pressure"`, {
                description: loc.alerts[0]?.msg || "Monitor closely",
                duration: 6000,
              });
            } else if ((cur === "Strong" || cur === "Stable") && (prev === "Under Pressure" || critical.includes(prev))) {
              toast.success(`${loc.name} recovered to "${cur}"`, { duration: 5000 });
            }
          }
          prevStatusRef.current[loc.location_id] = cur;
        }
      } else if (newData.locations) {
        for (const loc of newData.locations) {
          prevStatusRef.current[loc.location_id] = loc.health.status;
        }
      }

      setData(newData);
      setLastRefresh(new Date());
      setCountdown(60);
    } catch (e) {
      if (isInitial) toast.error("Failed to load pulse data");
    }
    if (isInitial) setLoading(false);
  }, []);

  // Initial load
  useEffect(() => { fetchPulse(true); }, [fetchPulse]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchPulse(false), 60000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchPulse]);

  // Countdown timer
  useEffect(() => {
    if (autoRefresh) {
      countdownRef.current = setInterval(() => {
        setCountdown(prev => (prev <= 1 ? 60 : prev - 1));
      }, 1000);
    }
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [autoRefresh]);

  const manualRefresh = () => { fetchPulse(false); setCountdown(60); };

  const openDetail = (loc) => { setSelectedLoc(loc); setDrawerOpen(true); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  // Filter only real locations (exclude TEST_ prefixed)
  let locations = (data.locations || []).filter(l => !l.name.startsWith("TEST_") && !l.name.startsWith("UI Test"));

  // Search
  if (search) locations = locations.filter(l => l.name.toLowerCase().includes(search.toLowerCase()));

  // Sort
  const sortFn = {
    health: (a, b) => { const order = ["Needs Attention","At Risk","Under Pressure","Busy but Healthy","Stable","Strong"]; return order.indexOf(a.health.status) - order.indexOf(b.health.status); },
    revenue: (a, b) => b.total_revenue - a.total_revenue,
    utilization: (a, b) => b.utilization - a.utilization,
    alerts: (a, b) => b.alerts.length - a.alerts.length,
    cancellations: (a, b) => b.cancel_count - a.cancel_count,
  };
  if (sortFn[sortBy]) locations.sort(sortFn[sortBy]);

  const s = data.summary;

  return (
    <div data-testid="location-pulse-board" className="min-h-screen bg-[#FAFAFA]">
      <DetailDrawer loc={selectedLoc} open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between animate-fadeIn">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl gradient-secondary flex items-center justify-center shadow-lg shadow-violet-500/20">
              <Activity className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight">Location Pulse Board</h1>
              <p className="text-sm text-slate-400 font-['Manrope']">Live operational overview across all locations</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Live indicator */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="auto-refresh-toggle"
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-medium font-['Manrope'] transition-all duration-200 ${
                autoRefresh
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-slate-50 border-slate-200 text-slate-500"
              }`}
            >
              {autoRefresh ? (
                <>
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                  </span>
                  LIVE
                  <span className="text-emerald-500/70 text-xs tabular-nums">{countdown}s</span>
                </>
              ) : (
                <>
                  <Pause className="w-3.5 h-3.5" strokeWidth={2} />
                  Paused
                </>
              )}
            </button>
            <Button onClick={manualRefresh} variant="outline" className="border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 rounded-xl h-10 gap-2 font-['Manrope']" data-testid="refresh-pulse-btn">
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
            </Button>
          </div>
        </div>

        {/* Executive Summary Strip */}
        <div data-testid="executive-summary" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" style={{ animation: "fadeSlideUp 0.4s ease-out both" }}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-5">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope'] mb-1">Locations Open</p>
              <p className="text-2xl font-semibold text-slate-900 font-['Outfit']">{s.locations_open}<span className="text-sm text-slate-400 ml-1">/ {s.total_locations}</span></p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope'] mb-1">At Risk</p>
              <p className={`text-2xl font-semibold font-['Outfit'] ${s.locations_at_risk > 0 ? 'text-rose-500' : 'text-slate-900'}`}>{s.locations_at_risk}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope'] mb-1">Under Pressure</p>
              <p className={`text-2xl font-semibold font-['Outfit'] ${s.locations_under_pressure > 0 ? 'text-amber-500' : 'text-slate-900'}`}>{s.locations_under_pressure}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope'] mb-1">Total Revenue</p>
              <p className="text-2xl font-semibold text-slate-900 font-['Outfit']">${s.total_revenue.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope'] mb-1">Staffing Gaps</p>
              <p className={`text-2xl font-semibold font-['Outfit'] ${s.total_staffing_gaps > 0 ? 'text-rose-500' : 'text-slate-900'}`}>{s.total_staffing_gaps}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope'] mb-1">Cancellations</p>
              <p className={`text-2xl font-semibold font-['Outfit'] ${s.total_cancellations > 2 ? 'text-amber-500' : 'text-slate-900'}`}>{s.total_cancellations}</p>
            </div>
          </div>
          {/* Escalation summary row */}
          {(s.total_escalations > 0 || s.total_urgent_escalations > 0) && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-400" strokeWidth={1.5} />
                <span className="text-xs font-semibold text-slate-700 font-['Manrope']">{s.total_escalations} open escalation{s.total_escalations !== 1 ? "s" : ""}</span>
              </div>
              {s.total_urgent_escalations > 0 && (
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-rose-500 text-white">{s.total_urgent_escalations} URGENT</span>
              )}
              <a href="/team-hub" className="ml-auto text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors font-['Manrope']">View in Team Hub →</a>
            </div>
          )}
          {s.strongest_location && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100 text-xs font-['Manrope']">
              <div className="flex items-center gap-4">
                <span className="text-emerald-600 font-semibold flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Strongest: {s.strongest_location}</span>
                <span className="text-slate-300">|</span>
                <span className="text-amber-600 font-semibold flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" /> Needs focus: {s.weakest_location}</span>
              </div>
              {lastRefresh && (
                <span className="text-slate-400" data-testid="last-refresh-time">
                  Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Smart Insights */}
        {data.insights && data.insights.length > 0 && (
          <div data-testid="smart-insights" className="bg-gradient-to-r from-violet-50 to-rose-50 border border-violet-100/50 rounded-2xl p-5" style={{ animation: "fadeSlideUp 0.5s ease-out both" }}>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-4 h-4 text-violet-500" strokeWidth={1.5} />
              <h3 className="text-sm font-semibold text-violet-800 font-['Outfit']">Smart Insights</h3>
            </div>
            <div className="space-y-2">
              {data.insights.map((insight, i) => {
                const text = typeof insight === "string" ? insight : insight.text;
                const type = typeof insight === "object" ? insight.type : "info";
                const typeConfig = {
                  escalation_urgent: { icon: AlertTriangle, color: "text-rose-500", bg: "bg-rose-100" },
                  escalation_pattern: { icon: AlertTriangle, color: "text-orange-500", bg: "bg-orange-100" },
                  escalation_ops: { icon: Wrench, color: "text-amber-600", bg: "bg-amber-100" },
                  escalation_quality: { icon: GraduationCap, color: "text-violet-500", bg: "bg-violet-100" },
                  escalation_summary: { icon: MessageSquare, color: "text-rose-500", bg: "bg-rose-100" },
                  pressure: { icon: Zap, color: "text-amber-500", bg: "bg-amber-100" },
                  opportunity: { icon: Star, color: "text-emerald-500", bg: "bg-emerald-100" },
                  positive: { icon: TrendingUp, color: "text-emerald-500", bg: "bg-emerald-100" },
                  cancellations: { icon: X, color: "text-amber-500", bg: "bg-amber-100" },
                  staffing: { icon: Users, color: "text-rose-500", bg: "bg-rose-100" },
                };
                const cfg = typeConfig[type] || { icon: Lightbulb, color: "text-violet-500", bg: "bg-violet-100" };
                const InsightIcon = cfg.icon;
                return (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className={`w-5 h-5 rounded-md ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <InsightIcon className={`w-3 h-3 ${cfg.color}`} strokeWidth={2} />
                    </div>
                    <p className="text-sm text-slate-600 font-['Manrope']">{text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search locations..."
              className="pl-10 border-slate-200 h-11 bg-white rounded-xl font-['Manrope'] text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500"
              data-testid="pulse-search" />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48 h-11 border-slate-200 rounded-xl font-['Manrope'] text-sm bg-white" data-testid="pulse-sort">
              <Filter className="w-4 h-4 mr-2 text-slate-400" strokeWidth={1.5} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Location Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {locations.map((loc, idx) => (
            <div key={loc.location_id} style={{ animation: `fadeSlideUp 0.4s ease-out ${idx * 80}ms both` }}>
              <LocationCard loc={loc} onClick={openDetail} />
            </div>
          ))}
        </div>

        {locations.length === 0 && (
          <div className="bg-white border border-slate-100 rounded-2xl p-12 text-center">
            <p className="text-slate-400 font-['Manrope']">No locations match your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}
